import React, { useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Form } from 'react-bootstrap'
import { Controller, useForm } from 'react-hook-form'
import { useResetMutation } from '../../redux/services/authService'
import { sweetAlert } from '../../utils'
import LoadingButton from '../../components/LoadingButton'
import { responseError } from '../../helpers/responseHandler'

const ResetPage = () => {
  const navigate = useNavigate()
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm()

  const [forgotRequest, { isLoading }] = useResetMutation()
  const otpRefs = useRef([])

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  // Password Regex
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/

  // Handle OTP typing + backspace
  const handleOtpChange = (e, index) => {
    const value = e.target.value
    setValue(`otp[${index}]`, value)

    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus()
    }
  }

  const handleOtpKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !e.target.value && index > 0) {
      otpRefs.current[index - 1]?.focus()
      setValue(`otp[${index - 1}]`, '')
    }
  }

  const onForgot = async data => {
    const otpValue = (data?.otp || []).join('')
    const payload = {
      ...data,
      otp: otpValue,
    }

    await forgotRequest(payload)
      .unwrap()
      .then(res => {
        if (res?.status) {
          sweetAlert(res?.message, 'success')
          navigate('/sign-in')
          window.localStorage.clear()
        }
        if (res?.errors) {
          Object.entries(res?.errors)?.forEach(([key, value]) => {
            sweetAlert(value[0], 'error')
          })
        }
      })
      .catch(err => {
        responseError(err)
      })
  }

  return (
    <div className='page login-img d-flex'>
      <div className='container-lg'>
        <div className='row justify-content-center mt-4 mx-0'>
          <div className='col-xl-4 col-lg-6 col-md-6 col-sm-6'>
            <figure className='text-center'>
              <img src='/logo_truklex.svg' alt='' className='logo_png' />
            </figure>
            <div className='card shadow-none'>
              <div className='card-body p-sm-6'>
                <div className='text-center mb-4'>
                  <h4 className='mb-1'>Reset Password</h4>
                </div>

                <Form onSubmit={handleSubmit(onForgot)} className='row'>
                  {/* OTP INPUT */}
                  <div className='col-sm-12'>
                    <div className='mb-3'>
                      <label className='mb-2 fw-500'>OTP</label>

                      <div className='d-flex justify-content-between gap-2'>
                        {[...Array(6)].map((_, index) => (
                          <Controller
                            key={index}
                            name={`otp[${index}]`}
                            control={control}
                            defaultValue=''
                            rules={{ required: true }}
                            render={({ field }) => (
                              <Form.Control
                                {...field}
                                ref={el => (otpRefs.current[index] = el)}
                                type='text'
                                maxLength={1}
                                className='text-center text-md'
                                onChange={e => {
                                  field.onChange(e) // ✅ ye zaroori hai
                                  handleOtpChange(e, index)
                                }}
                                onKeyDown={e => handleOtpKeyDown(e, index)}
                                isInvalid={errors?.otp?.[index]}
                              />
                            )}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* NEW PASSWORD */}
                  <div className='col-sm-12 position-relative'>
                    <div className='mb-3'>
                      <label className='mb-2 fw-500'>New Password</label>
                      <div className='position-relative'>
                        <Controller
                          name='password'
                          control={control}
                          defaultValue=''
                          rules={{
                            required: 'Password is required',
                            pattern: {
                              value: passwordRegex,
                              message:
                                'Password must be 8+ chars, include upper, lower, number, special & no spaces.',
                            },
                          }}
                          render={({ field }) => (
                            <Form.Control
                              {...field}
                              className='form-control ms-0'
                              type={showPassword ? 'text' : 'password'}
                              placeholder='Enter new password'
                              isInvalid={errors.password}
                            />
                          )}
                        />
                        {errors.password && (
                          <small className='text-danger'>{errors.password.message}</small>
                        )}
                        <button
                          type='button'
                          onClick={() => setShowPassword(!showPassword)}
                          className='position-absolute top-0 end-0 border-0 bg-transparent p-2 text-orange'
                          style={{ boxShadow: 'none' }}
                        >
                          {showPassword ? (
                            <i className='ri-eye-off-fill text-orange' />
                          ) : (
                            <i className='ri-eye-fill text-orange' />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* CONFIRM PASSWORD */}
                  <div className='col-sm-12 position-relative'>
                    <div className='mb-3'>
                      <label className='mb-2 fw-500'>Confirm Password</label>
                      <div className='position-relative'>
                        <Controller
                          name='confirm_password'
                          control={control}
                          defaultValue=''
                          rules={{
                            required: 'Confirm password is required',
                            validate: value =>
                              value === watch('password') || 'Passwords do not match!',
                          }}
                          render={({ field }) => (
                            <Form.Control
                              {...field}
                              className='form-control ms-0'
                              type={showConfirmPassword ? 'text' : 'password'}
                              placeholder='Confirm password'
                              isInvalid={errors.confirm_password}
                            />
                          )}
                        />
                        {errors.confirm_password && (
                          <small className='text-danger'>{errors.confirm_password.message}</small>
                        )}
                        <button
                          type='button'
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className='position-absolute top-0 end-0 border-0 bg-transparent p-2'
                          style={{ boxShadow: 'none' }}
                        >
                          {showConfirmPassword ? (
                            <i className='ri-eye-off-fill  text-orange' />
                          ) : (
                            <i className='ri-eye-fill  text-orange' />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className='col-xl-12'>
                    <div className='d-grid mb-3'>
                      <LoadingButton isLoading={isLoading} name={'Submit'} />
                    </div>
                    <div className='text-center'>
                      <p className='mb-0 tx-14'>
                        Remembered your password?
                        <Link to='/sign-in' className='tx-primary ms-1 text-decoration-underline'>
                          Sign In
                        </Link>
                      </p>
                    </div>
                  </div>
                </Form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ResetPage
